import React, { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import axios from "axios";
import MainHeader from "../Sidebar/MainHeader";
import { Notyf } from "notyf";
import { API_BASE_URL, ENDPOINTS } from '../../config';

const notyf = new Notyf({
  position: { x: "right", y: "top" },
  ripple: true,
  dismissible: true,
});

const Settings = () => {
  const [activeTab, setActiveTab] = useState("general");
  const [cookies] = useCookies(["token"]);
  const [user, setUser] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);

  const tabs = [
    { id: "general", icon: "fas fa-cog", label: "Profile Settings" },
    { id: "notifications", icon: "fas fa-bell", label: "Security" },
    { id: "security", icon: "fas fa-shield-alt", label: "Appearance" },
  ];

  const API_BASE = API_BASE_URL || "https://ai.holaai.in";



  const [theme, setTheme] = useState(() =>
    typeof window !== "undefined" ? document.documentElement.getAttribute("data-theme") || "light" : "light"
  );

  useEffect(() => {
    const handler = () => setTheme(document.documentElement.getAttribute("data-theme"));
    // observe attribute changes (optional)
    const obs = new MutationObserver(handler);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);

  const onToggle = () => {
    if (window.toggleTheme) {
      const next = window.toggleTheme();
      setTheme(next);
    } else {
      // fallback
      const next = theme === "dark" ? "light" : "dark";
      document.documentElement.setAttribute("data-theme", next);
      localStorage.setItem("app_theme", next);
      setTheme(next);
    }
  };
  // Fetch crent user on mount
  useEffect(() => {
    const token = cookies.token;
    if (!token) return;

    axios
      .get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const data = res.data;
        setUser(data);
        const name = `${data.first_name || ""} ${data.last_name || ""}`.trim();
        setDisplayName(name || (data.email ? data.email.split("@")[0] : ""));
      })
      .catch((err) => {
        console.error("Failed to load user:", err);
        notyf.error("Failed to load profile.");
      });
  }, [cookies.token, API_BASE]);

  // Save handler
  async function handleSaveName(e) {
    e.preventDefault();
    const token = cookies.token;
    if (!token) {
      notyf.error("You must be logged in to update profile.");
      return;
    }

    setSaving(true);

    const parts = (displayName || "").trim().split(/\s+/).filter(Boolean);
    const first_name = parts.length > 0 ? parts[0] : null;
    const last_name = parts.length > 1 ? parts.slice(1).join(" ") : null;

    try {
      const res = await axios.patch(
        `${API_BASE}/auth/me`,
        { first_name, last_name },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = res.data;
      setUser(data);
      const name = `${data.first_name || ""} ${data.last_name || ""}`.trim();
      setDisplayName(name || (data.email ? data.email.split("@")[0] : ""));
      notyf.success("Profile updated successfully.");
    } catch (err) {
      console.error("Failed to save profile:", err);
      const message =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message ||
        "Network error while saving.";
      notyf.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="main-content">
      <MainHeader />
      <div className="container-fluid py-4 px-4">
        <h4>Account Settings</h4>
        <p>Manage Your Account</p>
        <div className="content">
          <div className="row">
            {/* Sidebar tabs */}
            <div className="col-lg-3">
              <div className="dashboard-card">
                <div className="card-body">
                  <nav className="settings-nav">
                    {tabs.map((tab) => (
                      <a
                        key={tab.id}
                        href={`#${tab.id}`}
                        className={`settings-nav-item ${activeTab === tab.id ? "active" : ""
                          }`}
                        onClick={(e) => {
                          e.preventDefault();
                          setActiveTab(tab.id);
                        }}
                      >
                        <i className={tab.icon}></i>
                        <span>{tab.label}</span>
                      </a>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Main content */}
            <div className="col-lg-9">
              {/* Profile Settings */}
              <div
                className={`settings-tab ${activeTab === "general" ? "active" : ""
                  }`}
                id="general"
              >
                <div className="dashboard-card">
                  <div className="card-header">
                    <h5 className="card-title">Profile Settings</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSaveName}>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label htmlFor="firstName" className="form-label">
                            Display Name
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="firstName"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                          />
                        </div>
                        <div className="col-md-6">
                          <label htmlFor="email" className="form-label">
                            Email
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            value={user?.email || ""}
                            readOnly
                          />
                        </div>
                      </div>
                      <div className="mt-4">
                        <button
                          type="submit"
                          className="btn btn-xs btn-primary"
                          disabled={saving}
                        >
                          {saving ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              {/* Security tab */}
              <div
                className={`settings-tab ${activeTab === "notifications" ? "active" : ""
                  }`}
                id="notifications"
              >
                <div className="dashboard-card">
                  <div className="card-header">
                    <h5 className="card-title">Security</h5>
                  </div>
                  <div className="card-body">
                    <div className="notification-section">
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="emailNews"
                            />
                            <label
                              className="form-check-label"
                              htmlFor="emailNews"
                            >
                              Two-Factor Authentication
                            </label>
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="emailSecurity"
                              defaultChecked
                            />
                            <label
                              className="form-check-label"
                              htmlFor="emailSecurity"
                            >
                              Activity Notifications
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <button
                          type="button"
                          className="btn btn-xs btn-primary"
                        >
                          Save Changes
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Appearance tab */}
              <div
                className={`settings-tab ${activeTab === "security" ? "active" : ""
                  }`}
                id="security"
              >
                <div className="dashboard-card">
                  <div className="card-header">
                    <h5 className="card-title">Appearance Settings</h5>
                  </div>
                  <div className="card-body">
                    <div className="d-flex">
                      <div onClick={onToggle} className={`theme-option dark ${theme === "dark" ? "active" : ""}`}>
                        <div className="theme-preview"></div>
                        <span>{theme === "dark" ? "Dark" : "Light"}</span>
                      </div>
                      {/* <div onClick={onToggle} className={`theme-option  ${theme === "light" ? "active" : ""}`}>
                        <div className="theme-preview"></div>
                        <span>Light</span>
                      </div>
                      <button onClick={onToggle} className="btn-icon" aria-label="Toggle theme">
                        {theme === "dark" ? <i className="fas fa-sun" /> : <i className="fas fa-moon" />}
                      </button> */}
                    </div>
                  </div>
                </div>
              </div>
              {/* End Appearance */}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Settings;
